// Iter-207n — Unified Drive overview dashboard (4 phases aggregated).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  HardDrive, RefreshCw, Loader2, AlertCircle, CheckCircle2, Settings,
  ExternalLink, FileText, Users, Receipt, Wallet,
} from "lucide-react";

const PHASE_META = {
  cra: {
    label: "CRA mensuels",
    icon: FileText,
    href: "/admin/cra-drive-health-check",
    description: "Compte-rendus d'activité archivés sur Drive (1 PDF / facture).",
  },
  suppliers: {
    label: "Fournisseurs",
    icon: Users,
    href: "/admin/suppliers-drive-health",
    description: "Dossiers fournisseurs : BC envoyés + factures reçues.",
  },
  cabinet_expenses: {
    label: "Dépenses cabinet",
    icon: Receipt,
    href: "/admin/cabinet-expenses-drive-health",
    description: "Justificatifs des dépenses internes (par année/mois).",
  },
  ndf: {
    label: "Notes de frais",
    icon: Wallet,
    href: "/admin/ndf-drive-health",
    description: "NDF collaborateurs (par utilisateur / statut).",
  },
};

function statusBadge(p) {
  if (p.status === "not_configured") return { color: "amber", label: "À configurer", icon: Settings };
  if (p.status === "error") return { color: "red", label: "Erreur", icon: AlertCircle };
  if (p.remediable_count > 0) return { color: "amber", label: `${p.remediable_count} à corriger`, icon: AlertCircle };
  return { color: "emerald", label: "OK", icon: CheckCircle2 };
}

function GlobalStatusPill({ status, summary }) {
  if (status === "needs_setup") {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-mono uppercase tracking-wider" data-testid="drive-overview-global-pill">
        <Settings size={13} /> {summary.not_configured} phase(s) à configurer
      </div>
    );
  }
  if (status === "warning") {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-mono uppercase tracking-wider" data-testid="drive-overview-global-pill">
        <AlertCircle size={13} /> {summary.total_remediable} élément(s) à corriger sur l'ensemble des phases
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-mono uppercase tracking-wider" data-testid="drive-overview-global-pill">
      <CheckCircle2 size={13} /> Drive — tout est conforme
    </div>
  );
}

export default function DriveOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/drive-overview");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement du dashboard Drive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="drive-overview-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
          <HardDrive size={22} /> Drive — Vue d'ensemble
        </h1>
        <p className="text-sm text-zinc-600 max-w-3xl leading-relaxed">
          État de conformité global des 4 intégrations Google Drive : CRA mensuels, dossiers fournisseurs, justificatifs cabinet, et notes de frais. Cliquez sur une carte pour ouvrir le Health Check détaillé de la phase.
        </p>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={load} disabled={loading}
          data-testid="drive-overview-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-xs font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Relancer
        </button>
        {data && <GlobalStatusPill status={data.global_status} summary={data.summary || {}} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!data && (
          <div className="col-span-2 p-12 text-center text-zinc-500 text-sm">
            {loading ? "Chargement…" : "Aucune donnée."}
          </div>
        )}
        {data?.phases?.map((p) => {
          const meta = PHASE_META[p.phase] || { label: p.phase, icon: HardDrive, href: "#" };
          const badge = statusBadge(p);
          const Icon = meta.icon;
          const BadgeIcon = badge.icon;
          return (
            <Link
              key={p.phase}
              to={meta.href}
              data-testid={`drive-overview-card-${p.phase}`}
              className="group block p-5 rounded-lg border border-zinc-200 bg-white hover:border-zinc-400 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-zinc-500" />
                    <h2 className="text-base font-medium text-zinc-900">{meta.label}</h2>
                  </div>
                  <p className="text-xs text-zinc-500 leading-snug">{meta.description}</p>
                </div>
                <ExternalLink size={14} className="text-zinc-400 group-hover:text-zinc-700" />
              </div>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-${badge.color}-200 bg-${badge.color}-50 text-${badge.color}-700`}>
                  <BadgeIcon size={11} /> {badge.label}
                </span>
                <span className="text-[11px] text-zinc-500 font-mono" data-testid={`drive-overview-${p.phase}-scanned`}>
                  {p.scanned} élément(s) scanné(s)
                </span>
              </div>

              {p.status === "configured" && Object.keys(p.counters || {}).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono">
                  {Object.entries(p.counters).map(([k, v]) => (
                    <span key={k} className="text-zinc-600">
                      <strong className="text-zinc-900">{v}</strong> {k}
                    </span>
                  ))}
                </div>
              )}

              {p.status === "not_configured" && (
                <p className="mt-3 text-[11px] text-amber-700 leading-snug">
                  → {p.message}
                </p>
              )}

              {p.status === "error" && (
                <p className="mt-3 text-[11px] text-red-700 leading-snug" data-testid={`drive-overview-${p.phase}-error`}>
                  ⚠ {p.message}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
